/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: hooks/useUsersList.ts
 🗓️ Created: 2025-12-02
 🧩 Description:
    Hook for fetching users list for security log filters
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/auth';
import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';

export interface UserOption {
  id: number;
  username: string;
  fullName: string | null;
  email: string;
}

export function useUsersList() {
  const { user: currentUser } = useAuthStore();
  const tenantSlug = currentUser?.tenant?.slug || '';
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!tenantSlug) return;
      
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: '100',
          sortBy: 'username',
          sortOrder: 'asc',
        });

        const response = await fetch(
          `${buildApiUrl(API_CONFIG.ENDPOINTS.USERS)}?${params}`,
          {
            method: 'GET',
            headers: getApiHeaders(tenantSlug, currentUser?.id),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const result = await response.json();
        setUsers(Array.isArray(result?.data) ? result.data : []);
      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [tenantSlug, currentUser?.id]);

  return { users, loading };
}

